import {
  TableBody as TableBodyDS,
  TableCell,
  TableRow,
} from '@cambridgeassessment/cambridge-ui';
import * as React from 'react';
import { memo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTableBodyStyles } from './styles';
import { DEFAULT_ROW_HEIGHT } from './Table';
import { Row } from './types';

type Props = {
  rows: Row[];
  emptyRows: number;
  colSpan?: number;
  rowHeight?: number;
  fixedRowHeight?: boolean;
  rowsPerPage?: number;
};

const TableBody: React.FC<Props> = ({
  rows,
  emptyRows,
  colSpan = 1,
  rowHeight = DEFAULT_ROW_HEIGHT,
  fixedRowHeight = false,
  rowsPerPage,
}) => {
  const { clickable } = useTableBodyStyles();
  const history = useHistory();

  const handleRedirect = useCallback(
    (event: any, link: string | undefined) => {
      if (
        event.target.matches('a') ||
        event.target.matches('button') ||
        event.target.matches('span')
      ) {
        return;
      }
      if (link) {
        history.push(link);
      }
    },
    [history]
  );

  const rowStyle = fixedRowHeight
    ? {
        height: rowHeight,
        maxHeight: rowHeight,
        overflow: 'hidden' as const,
      }
    : undefined;

  const cellStyle = fixedRowHeight
    ? {
        height: rowHeight,
        maxHeight: rowHeight,
        overflow: 'hidden' as const,
        boxSizing: 'border-box' as const,
        paddingTop: 6,
        paddingBottom: 6,
        lineHeight: 1.25,
      }
    : undefined;

  const bodyStyle =
    fixedRowHeight && rowsPerPage
      ? {
          height: rowsPerPage * rowHeight,
          minHeight: rowsPerPage * rowHeight,
          verticalAlign: 'top' as const,
        }
      : undefined;

  const emptyRowStyle = rowStyle ?? { height: rowHeight };
  const emptyCellStyle = cellStyle ?? {
    height: rowHeight,
    padding: 0,
    border: 0,
  };

  return (
    <TableBodyDS data-testid="user-table-body" style={bodyStyle}>
      {rows.map((row, index) => (
        <TableRow
          hover
          key={`row-${index}`}
          onClick={(event) => handleRedirect(event, row.link)}
          className={row.link ? clickable : null}
          style={rowStyle}
        >
          {Object.entries(row.cells).map(([oid, content], idx) => (
            <TableCell key={`${oid}-${idx}`} style={cellStyle}>
              {content as React.ReactNode}
            </TableCell>
          ))}
        </TableRow>
      ))}
      {Array.from({ length: emptyRows }).map((_, index) => (
        <TableRow key={`empty-row-${index}`} aria-hidden style={emptyRowStyle}>
          <TableCell colSpan={colSpan} style={emptyCellStyle} />
        </TableRow>
      ))}
    </TableBodyDS>
  );
};

export default memo(TableBody);
